import { ref, watch } from "vue";
import { Logger } from "@/core/utilities";
import { useUser } from "@/shared/account";

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    Raychat?: {
      setUser: (userData: {
        email?: string;
        name?: string;
        phone?: string;
        updateOnce?: boolean;
      }) => void;
    };
    RAYCHAT_TOKEN?: string;
    LOAD_TYPE?: string;
  }
}

const RAYCHAT_TOKEN = import.meta.env.APP_RAYCHAT_TOKEN as string | undefined;
const RAYCHAT_WIDGET_URL = import.meta.env.APP_RAYCHAT_WIDGET_URL as string | undefined;

const isRaychatReady = ref(false);
const isRaychatScriptLoaded = ref(false);
const pendingUserData = ref<{
  email?: string;
  name?: string;
  phone?: string;
} | null>(null);

export function useRaychat() {
  const { user, isAuthenticated } = useUser();

  /**
   * Load Raychat script dynamically
   */
  function loadRaychatScript() {
    if (typeof window === "undefined" || isRaychatScriptLoaded.value) {
      return;
    }

    // Check if Raychat is configured
    if (!RAYCHAT_TOKEN || !RAYCHAT_WIDGET_URL) {
      Logger.warn("useRaychat", "Raychat token or widget URL not configured");
      return;
    }

    try {
      // Set Raychat configuration
      window.RAYCHAT_TOKEN = RAYCHAT_TOKEN;
      window.LOAD_TYPE = "SEO_FRIENDLY";

      // Create and append script element
      const script = document.createElement("script");
      script.src = RAYCHAT_WIDGET_URL;
      script.async = true;
      document.getElementsByTagName("head")[0].appendChild(script);

      isRaychatScriptLoaded.value = true;
      Logger.debug("useRaychat", "Raychat script loaded");
    } catch (error) {
      Logger.error("useRaychat", "Failed to load Raychat script", error);
    }
  }

  /**
   * Initialize Raychat ready listener
   * Should be called once when the app starts
   */
  function initRaychat() {
    if (typeof window === "undefined") {
      return;
    }

    // Load the Raychat script
    loadRaychatScript();

    // Listen for Raychat ready event
    window.addEventListener("raychat_ready", () => {
      Logger.debug("useRaychat", "Raychat is ready");
      isRaychatReady.value = true;

      // If there's pending user data, set it now
      if (pendingUserData.value) {
        setRaychatUser(pendingUserData.value);
        pendingUserData.value = null;
      }
    });

    // Watch for authentication changes
    watch(
      isAuthenticated,
      (authenticated) => {
        if (authenticated) {
          updateRaychatUser();
        }
      },
      { immediate: true }
    );
  }

  /**
   * Set user data in Raychat
   * @param userData - User data to send to Raychat
   */
  function setRaychatUser(userData: {
    email?: string;
    name?: string;
    phone?: string;
  }) {
    if (!window.Raychat?.setUser) {
      Logger.warn("useRaychat", "Raychat is not loaded yet, saving user data for later");
      pendingUserData.value = userData;
      return;
    }

    try {
      window.Raychat.setUser({
        email: userData.email,
        name: userData.name,
        phone: userData.phone,
        updateOnce: true,
      });
      Logger.debug("useRaychat", "User data set successfully", userData);
    } catch (error) {
      Logger.error("useRaychat", "Failed to set user data", error);
    }
  }

  /**
   * Update Raychat with current user data
   * Called automatically when user is authenticated
   */
  function updateRaychatUser() {
    if (!isAuthenticated.value || !user.value) {
      return;
    }

    const userData = {
      email: user.value.email || undefined,
      name: getFullName(),
      phone: getPhoneNumber(),
    };

    // If Raychat is ready, set user immediately, otherwise queue it
    if (isRaychatReady.value) {
      setRaychatUser(userData);
    } else {
      pendingUserData.value = userData;
    }
  }

  /**
   * Get user's full name
   */
  function getFullName(): string | undefined {
    if (!user.value?.contact) {
      return undefined;
    }

    const { firstName, lastName } = user.value.contact;
    const parts = [firstName, lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : undefined;
  }

  /**
   * Get user's phone number
   */
  function getPhoneNumber(): string | undefined {
    return user.value?.phoneNumber || undefined;
  }

  return {
    initRaychat,
    updateRaychatUser,
    isRaychatReady,
  };
}
